import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUSES = ["unfulfilled", "processing", "shipped", "delivered", "cancelled"];
const PAYMENT_COLORS = { paid: "text-green-400", pending: "text-yellow-400", failed: "text-red-400", refunded: "text-white/50", refund_failed: "text-red-400" };

const money = (sym, v) => `${sym || "$"}${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export const OrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, fulfillment_status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { fulfillment_status });
      toast.success("Order updated");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const paidOrders = orders.filter((o) => o.payment_status === "paid");

  return (
    <div data-testid="orders-panel">
      <p className="font-mono-label text-xs uppercase tracking-luxe text-white/40 mb-8">
        {orders.length} orders · {paidOrders.length} paid
      </p>
      <div className="border border-white/10 divide-y divide-white/10">
        {orders.length === 0 && (
          <p className="p-10 text-center text-white/40 font-mono-label text-sm uppercase tracking-luxe">No orders yet</p>
        )}
        {orders.map((o) => (
          <div key={o.id} data-testid={`admin-order-${o.order_number}`}>
            {o.payment_status === "refund_failed" && (
              <div data-testid={`refund-failed-${o.order_number}`} className="bg-red-500/15 border-b border-red-500/40 px-4 py-2">
                <p className="font-mono-label text-[10px] uppercase tracking-luxe text-red-300">
                  ⚠ Refund failed — customer was charged but auto-refund did not complete. Refund manually in Stripe.
                </p>
              </div>
            )}
            {o.oversold && o.payment_status !== "refund_failed" && (
              <div data-testid={`oversold-${o.order_number}`} className="bg-white/10 border-b border-white/20 px-4 py-2">
                <p className="font-mono-label text-[10px] uppercase tracking-luxe text-white/60">
                  Oversold — item already sold; this order was auto-refunded.
                </p>
              </div>
            )}
            <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setOpen(open === o.id ? null : o.id)}>
              <div className="flex-1 min-w-0">
                <p className="text-white font-mono-label text-sm">{o.order_number}</p>
                <p className="text-xs text-white/40 mt-1">
                  {o.customer?.name || "Guest"} · {o.customer?.email || "—"} · {new Date(o.created_at).toLocaleString()}
                </p>
              </div>
              <span className={`font-mono-label text-xs uppercase ${PAYMENT_COLORS[o.payment_status] || "text-white/50"}`}>
                {o.payment_status}
              </span>
              <span className="font-mono-label text-sm text-white w-28 text-right">{money(o.symbol, o.total)} {o.currency?.toUpperCase()}</span>
              <select
                data-testid={`order-status-${o.order_number}`}
                value={o.fulfillment_status}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => updateStatus(o.id, e.target.value)}
                className="bg-transparent border border-white/20 text-white/80 font-mono-label text-[10px] uppercase tracking-wider px-2 py-2 focus:outline-none focus:border-white/60"
              >
                {STATUSES.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
              </select>
            </div>
            {open === o.id && (
              <div className="px-4 pb-6 bg-white/[0.02]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                  <div>
                    <p className="font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-2">Items</p>
                    {o.items?.map((it, k) => (
                      <div key={k} className="flex justify-between text-sm text-white/70 py-1">
                        <span>{it.name} · {it.size} × {it.quantity}</span>
                        <span className="font-mono-label">{money(o.symbol, it.line_total)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-white/50 text-xs pt-2 border-t border-white/10 mt-2">
                      <span>Shipping</span><span className="font-mono-label">{o.shipping_fee === 0 ? "Free" : money(o.symbol, o.shipping_fee)}</span>
                    </div>
                    <div className="flex justify-between text-white text-sm pt-1">
                      <span>Total</span><span className="font-mono-label">{money(o.symbol, o.total)} {o.currency?.toUpperCase()}</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-2">Shipping to</p>
                    <p className="text-sm text-white/70 leading-relaxed">
                      {[o.shipping_address?.line1, o.shipping_address?.line2,
                        `${o.shipping_address?.postal_code || ""} ${o.shipping_address?.city || ""}`.trim(),
                        o.shipping_address?.country].filter(Boolean).map((l, i) => <span key={i}>{l}<br /></span>)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
